import React from 'react';

import styled from 'styled-components';

import { Loader } from 'src/app/components/lib/loader';

import { useWorkflowBuilderContext } from '../../../wizard/builder-context';
import { ActionTypeEnum, NodeType } from '../models';

import type { ActionType } from '../models';
import type {
  EmailDeliverability,
  PushDeliverability,
  SMSDeliverability,
} from 'src/app/queries/marketing/get-deliverability-metrics';
import type { WorkflowStepStats } from 'src/app/queries/marketing/get-workflow-execution-stats';

type DeliverabilityStatsConfig = {
  clickRate?: { count: number; label: string; value: number };
  openRate: { count: number; label: string; value: number };
  recipients: { label: string; value: number };
};

const defaultDeliverabilityStats = (isPushNode: boolean): DeliverabilityStatsConfig => ({
  clickRate: isPushNode ? undefined : { value: 0, count: 0, label: 'Click rate' },
  openRate: { value: 0, count: 0, label: 'Open rate' },
  recipients: { value: 0, label: 'Recipients' },
});

type ExecutionStats = {
  timesEntered: number;
  timesErrored: number;
};

const renderDeliverabilityStats = (
  executionStats: ExecutionStats,
  isLoading: boolean,
  deliverabilityStats?: EmailDeliverability | PushDeliverability | SMSDeliverability | null,
  nodeType?: ActionType | NodeType
) => {
  const getDeliverabilityStats = (): DeliverabilityStatsConfig => {
    if (!deliverabilityStats) {
      return defaultDeliverabilityStats(nodeType === 'sendPushNotification');
    }

    if (nodeType === 'sendEmail') {
      const { Opened, Clicked, Delivered } = deliverabilityStats as EmailDeliverability;
      const openRate = Delivered ? Math.round((Opened / Delivered) * 100) : 0;
      const clickRate = Delivered ? Math.round((Clicked / Delivered) * 100) : 0;

      return {
        recipients: { value: Delivered, label: 'Recipients' },
        openRate: { value: openRate, count: Opened, label: 'Open rate' },
        clickRate: { value: clickRate, count: Clicked, label: 'Click rate' },
      };
    }

    if (nodeType === 'sendSMS') {
      const { Delivered, LinkClicked, GatePassedThrough } = deliverabilityStats as SMSDeliverability;
      const openRate = Delivered ? Math.round((GatePassedThrough / Delivered) * 100) : 0;
      const clickRate = Delivered ? Math.round((LinkClicked / Delivered) * 100) : 0;

      return {
        recipients: { value: Delivered, label: 'Recipients' },
        openRate: { value: openRate, count: GatePassedThrough, label: 'Open rate' },
        clickRate: { value: clickRate, count: LinkClicked, label: 'Click rate' },
      };
    }

    if (nodeType === 'sendPushNotification') {
      const { Opened, Sent } = deliverabilityStats as PushDeliverability;
      const openRate = Sent ? Math.round((Opened / Sent) * 100) : 0;

      return {
        recipients: { value: Sent, label: 'Recipients' },
        openRate: { value: openRate, count: Opened, label: 'Open rate' },
      };
    }

    return defaultDeliverabilityStats(false);
  };

  const finalConfig = getDeliverabilityStats();

  return (
    <StatsBox data-testid='deliverability-stats-box'>
      <StatRow>
        <StatLabel>Executed</StatLabel>
        <StatValue>{executionStats.timesEntered}</StatValue>
      </StatRow>
      <StatRow>
        <StatLabel>Interrupted</StatLabel>
        <StatValue>{executionStats.timesErrored}</StatValue>
      </StatRow>

      <StatRow>
        <StatLabel>{finalConfig.recipients.label}</StatLabel>
        <StatValue>{isLoading ? <Loader size='xs' variant='grey' /> : finalConfig.recipients.value}</StatValue>
      </StatRow>
      <StatRow>
        <StatLabel>{finalConfig.openRate.label}</StatLabel>
        <StatValue>
          {isLoading ? (
            <Loader size='xs' variant='grey' />
          ) : (
            <>
              {finalConfig.openRate.value}% <StatCount>({finalConfig.openRate.count})</StatCount>
            </>
          )}
        </StatValue>
      </StatRow>
      {finalConfig.clickRate && (
        <StatRow>
          <StatLabel>{finalConfig.clickRate.label}</StatLabel>
          <StatValue>
            {isLoading ? (
              <Loader size='xs' variant='grey' />
            ) : (
              <>
                {finalConfig.clickRate.value}% <StatCount>({finalConfig.clickRate.count})</StatCount>
              </>
            )}
          </StatValue>
        </StatRow>
      )}
    </StatsBox>
  );
};

type NodeStatsProps = {
  deliverabilityStats?: EmailDeliverability | PushDeliverability | SMSDeliverability | null;
  isDeliverabilityMetricsLoading?: boolean;
  nodeType: ActionType | NodeType;
  showAnalytics?: boolean;
  stats?: WorkflowStepStats;
};

export function NewNodeStats({
  stats,
  deliverabilityStats,
  nodeType,
  isDeliverabilityMetricsLoading,
  showAnalytics = true,
}: NodeStatsProps) {
  const [builderState] = useWorkflowBuilderContext();
  const { isReadOnly } = builderState;

  const { TimesEntered: timesEntered, TimesErrored } = stats ?? { TimesEntered: 0, TimesErrored: 0 };
  const timesErrored =
    TimesErrored + (stats?.Interruptions?.reduce((acc, interruption) => acc + interruption.TimesOccurred, 0) ?? 0);

  // Only show stats in read-only mode (Content tab) and when showAnalytics is true
  if (!isReadOnly || !showAnalytics) {
    return null;
  }

  if (nodeType === 'sendEmail' || nodeType === 'sendSMS' || nodeType === 'sendPushNotification') {
    return renderDeliverabilityStats(
      { timesEntered, timesErrored },
      !!isDeliverabilityMetricsLoading,
      deliverabilityStats,
      nodeType
    );
  }

  if (
    nodeType === NodeType.TIME_DELAY ||
    nodeType === NodeType.TRIGGER ||
    nodeType === NodeType.CONDITIONAL ||
    nodeType === ActionTypeEnum.GRANT_POINTS
  ) {
    return (
      <StatsBox>
        <StatRow>
          <StatLabel>Executed</StatLabel>
          <StatValue>{timesEntered}</StatValue>
        </StatRow>
        <StatRow>
          <StatLabel>Interrupted</StatLabel>
          <StatValue>{timesErrored}</StatValue>
        </StatRow>
      </StatsBox>
    );
  }
  return null;
}

const StatsBox = styled.div`
  background: var(--color-gray-10);
  border-radius: var(--sizes-20);
  padding: var(--sizes-30) var(--sizes-40);
  margin-top: var(--sizes-20);
  font: var(--font-regular-14pt-normal);
  color: var(--color-grayscale-black);
  display: flex;
  flex-direction: column;
  gap: var(--sizes-20);
  width: 100%;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StatLabel = styled.span`
  color: var(--color-gray-70);
  font: var(--font-extra-small-12pt-normal);
  text-align: left;
`;

const StatValue = styled.span`
  color: var(--color-grayscale-black);
  font: var(--font-extra-small-12pt-semibold);
  text-align: right;
`;

const StatCount = styled.span`
  color: var(--color-gray-70);
  font: var(--font-extra-small-12pt-normal);
`;
